import {
  ENTER_DUR,
  HOLD_DUR,
  R_CORRIDOR,
  R_ENTRY,
  R_SPAWN,
  TAU,
  WAVE_CLEAR_BONUS,
} from '../constants';
import { angleDelta, clamp, corridorOf, norm } from '../polar';
import { rand, randRange } from '../rng';
import { acquireEnemy, pushEvent } from '../world';
import type { Content, Enemy, EnemyDef, PendingSpawn, World } from '../types';
import { collectAll } from './drops';

/**
 * Where an enemy crosses the corridor edge. It enters near the pole of its
 * nearer corridor, leaning toward its final bearing — so a due-east target
 * comes in through a corner and curves, exactly as §6.1 describes, while a due
 * north target drops straight down the middle.
 */
function entryAngleFor(bearing: number): number {
  const pole = corridorOf(bearing);
  const lean = angleDelta(pole, bearing);
  // Cap the lean so entries stay inside the corridor rather than sliding round
  // to the flanks, which would kill the "watch it form" read.
  return pole + clamp(lean, -0.62, 0.62);
}

export function spawnEnemy(
  world: World,
  def: EnemyDef,
  bearing: number,
  stageOverride?: number,
): Enemy | null {
  const e = acquireEnemy(world);
  if (!e) return null;

  const b = norm(bearing);
  e.alive = true;
  e.def = def;
  e.bearing = b;
  e.entryTheta = entryAngleFor(b);
  e.theta = e.entryTheta;
  e.r = R_ENTRY;
  e.phase = 'enter';
  e.phaseT = 0;
  e.phaseR = R_ENTRY;
  e.phaseTheta = e.entryTheta;
  e.stageDur = stageOverride ?? randRange(world.rng, def.stageMin, def.stageMax);
  e.hp = def.hp;
  e.maxHp = def.hp;
  e.speed = def.speed * (world.passive.enemySpeedMult ?? 1);
  e.stunT = 0;
  e.freezeT = 0;
  e.slowT = 0;
  e.slowMult = 1;
  e.burnT = 0;
  e.burnDps = 0;
  e.timer = 0;
  e.veered = false;
  e.underRing = false;
  e.cloaked = def.behaviour === 'cloak';
  e.revealed = false;
  e.phaseIndex = 0;
  e.wobble = rand(world.rng) * TAU;
  return e;
}

/**
 * Spawns a child directly into its attack run at a given point — asteroid
 * splits and carrier launches. No staging flight; they're already in.
 *
 * Takes a position rather than a parent entity on purpose. A dead parent's
 * pool slot is immediately reusable, so the first child of a split is often
 * the parent's own object; reading anything off the parent after that point
 * reads the child instead.
 */
export function spawnChildAt(
  world: World,
  content: Content,
  childId: string,
  theta: number,
  r: number,
  underRing: boolean,
): Enemy | null {
  const def = content.enemies.get(childId);
  if (!def) return null;
  const e = acquireEnemy(world);
  if (!e) return null;

  e.alive = true;
  e.def = def;
  e.bearing = norm(theta);
  e.entryTheta = e.bearing;
  e.theta = e.bearing;
  e.r = r;
  e.phase = 'attack';
  e.phaseT = 0;
  e.phaseR = r;
  e.phaseTheta = e.theta;
  e.stageDur = 0;
  e.hp = def.hp;
  e.maxHp = def.hp;
  e.speed = def.speed * (world.passive.enemySpeedMult ?? 1);
  e.stunT = 0;
  e.freezeT = 0;
  e.slowT = 0;
  e.slowMult = 1;
  e.burnT = 0;
  e.burnDps = 0;
  e.timer = 0;
  e.veered = false;
  e.underRing = underRing;
  e.cloaked = def.behaviour === 'cloak';
  e.revealed = false;
  e.phaseIndex = 0;
  e.wobble = rand(world.rng) * TAU;
  return e;
}

/** Expand a wave definition into a time-ordered spawn queue. */
export function queueWave(world: World, content: Content): void {
  const wave = world.level.waves[world.waveIndex];
  if (!wave) return;

  const pending: PendingSpawn[] = [];
  const doubleBurrowers = world.modifiers.has('double-burrowers');

  for (const group of wave.groups) {
    const def = content.enemies.get(group.enemy);
    if (!def) continue;

    const base =
      group.bearing === 'random' ? rand(world.rng) * TAU : group.bearing;

    let count = group.count;
    if (doubleBurrowers && def.behaviour === 'burrow') count *= 2;

    for (let i = 0; i < count; i++) {
      // Fan the group across its spread, centred on the group bearing.
      const t = count === 1 ? 0.5 : i / (count - 1);
      const bearing = base + (t - 0.5) * group.fan;
      pending.push({
        at: group.at + i * group.gap,
        enemy: def,
        bearing,
        stage: group.stage ?? 0,
      });
    }
  }

  pending.sort((a, b) => a.at - b.at || a.bearing - b.bearing);
  world.pending = pending;
}

export function updateWave(world: World, content: Content, dt: number): void {
  const wave = world.level.waves[world.waveIndex];
  if (!wave) return;

  switch (world.waveState) {
    case 'prep': {
      // A wave's `prep` is an auto-start countdown; 0 means untimed, and the
      // wave waits for the player. Planning is where the whole build decision
      // lives now, so it doesn't get a clock ticking over it — all the time
      // pressure belongs inside the wave.
      if (world.waveTimer > 0) {
        world.waveTimer -= dt;
        if (world.waveTimer <= 0) startWave(world, content);
      }
      break;
    }
    case 'active': {
      world.waveTimer += dt;
      while (world.pending.length > 0 && world.pending[0]!.at <= world.waveTimer) {
        const p = world.pending.shift()!;
        spawnEnemy(world, p.enemy, p.bearing, p.stage > 0 ? p.stage : undefined);
      }
      if (world.pending.length === 0 && countLiving(world) === 0) {
        world.waveState = 'clearing';
        world.waveTimer = 0.6;
      }
      break;
    }
    case 'clearing': {
      world.waveTimer -= dt;
      if (world.waveTimer <= 0) finishWave(world, content);
      break;
    }
    default:
      break;
  }
}

export function startWave(world: World, content: Content): void {
  // Normally already queued when the build phase opened; this covers a wave
  // started before that ever happened.
  if (world.pending.length === 0) queueWave(world, content);
  world.waveState = 'active';
  world.waveTimer = 0;
  pushEvent(world, { type: 'wave-start', amount: world.waveIndex + 1 });
}

function finishWave(world: World, content: Content): void {
  // Anything still lying on the field is swept up as part of the payout. There
  // is no reward for panic-tapping the last few drops as the wave ends, and
  // making the player do it would be busywork rather than a decision.
  collectAll(world);

  // The clear bonus is flat. Per-pickup bonuses already paid out above.
  const wave = world.level.waves[world.waveIndex];
  const gain = (wave?.reward ?? 0) + WAVE_CLEAR_BONUS;
  world.juice += gain;
  world.stats.juiceEarned += gain;
  pushEvent(world, { type: 'wave-clear', amount: gain });

  world.waveIndex++;
  const next = world.level.waves[world.waveIndex];
  if (!next) {
    world.waveState = 'won';
    pushEvent(world, { type: 'level-won' });
    return;
  }
  world.waveState = 'prep';
  world.waveTimer = next.prep;
  // Resolve the next wave's bearings now, so the build phase can show exactly
  // where it's coming from — including the groups that pick a random bearing.
  queueWave(world, content);
}

export function countLiving(world: World): number {
  let n = 0;
  for (const e of world.enemies) if (e.alive) n++;
  return n;
}

export { ENTER_DUR, HOLD_DUR, R_CORRIDOR, R_SPAWN };
